import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Send, CheckCircle2, Download, HardHat } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

// PUBLIC page (no auth) — a sub sees SCOPE + QUANTITIES only and submits unit prices. Never shows WD pricing.
export default function RfqQuote() {
  const { token } = useParams();
  const [rfq, setRfq] = useState(null);
  const [err, setErr] = useState("");
  const [vendor, setVendor] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [rates, setRates] = useState({});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/rfq/${token}`).then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setRfq).catch(() => setErr("This RFQ link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    if (!vendor.trim()) { toast.error("Enter your company name."); return; }
    const lines = (rfq.scope || []).map((s, i) => ({ item: s.item, unit: s.unit, quantity: s.quantity, quoted_unit_rate: rates[i] }))
      .filter((l) => l.quoted_unit_rate !== undefined && l.quoted_unit_rate !== "" && !isNaN(Number(l.quoted_unit_rate)))
      .map((l) => ({ ...l, quoted_unit_rate: Number(l.quoted_unit_rate) }));
    if (!lines.length) { toast.error("Enter a unit price for at least one line."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/rfq/${token}/quote`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vendor, email, notes, lines }),
      });
      if (!r.ok) throw new Error();
      setSent(true);
    } catch { toast.error("Submission failed — try again."); }
    setBusy(false);
  };

  if (err) return <Center><div className="text-zinc-500" data-testid="rfq-error">{err}</div></Center>;
  if (!rfq) return <Center><div className="text-zinc-400">Loading RFQ…</div></Center>;
  if (sent) return (
    <Center>
      <div className="text-center" data-testid="rfq-sent">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <h2 className="font-display font-bold text-2xl text-zinc-900">Quote submitted</h2>
        <p className="text-sm text-zinc-500 mt-2">Thank you, {vendor}. Williams Diversified has received your pricing.</p>
      </div>
    </Center>
  );

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4" data-testid="rfq-public-page">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8">
        <div className="flex items-center gap-2 text-zinc-900">
          <HardHat className="h-6 w-6" />
          <span className="font-display font-extrabold text-xl">Williams Diversified — Request for Quote</span>
        </div>
        <h1 className="font-display font-bold text-2xl mt-3 text-zinc-900">{rfq.project_title}</h1>
        <div className="text-sm text-zinc-500 mt-1">
          {rfq.location && <span>{rfq.location} · </span>}{rfq.solicitation && <span>{rfq.solicitation} · </span>}<span className="font-mono">{rfq.code}</span>
        </div>
        <a href={`${API}/rfq/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="rfq-pdf-link"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-600 border border-zinc-300 rounded-lg px-3 py-1.5 mt-4 hover:bg-zinc-50">
          <Download className="h-4 w-4" /> Download scope PDF
        </a>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Your company name *" data-testid="rfq-vendor"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Contact email" data-testid="rfq-email"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
        </div>

        <div className="mt-5 border border-zinc-200 rounded-xl overflow-hidden" data-testid="rfq-scope-table">
          <div className="grid grid-cols-[1fr_70px_60px_120px] bg-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500 px-3 py-2">
            <span>Scope item</span><span className="text-right">Qty</span><span>Unit</span><span className="text-right">Your unit price</span>
          </div>
          {(rfq.scope || []).map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_60px_120px] items-center px-3 py-2 border-t border-zinc-100 text-sm">
              <span className="text-zinc-800 pr-2">{s.item}<span className="block text-[10px] text-zinc-400">{s.basis}</span></span>
              <span className="text-right font-mono text-zinc-600">{String(s.quantity)}</span>
              <span className="text-zinc-500 text-xs">{s.unit}</span>
              <input type="number" value={rates[i] ?? ""} onChange={(e) => setRates({ ...rates, [i]: e.target.value })}
                data-testid={`rfq-rate-${i}`} placeholder="$/unit"
                className="border border-zinc-300 rounded px-2 py-1 text-sm text-right w-full" />
            </div>
          ))}
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (exclusions, lead time, validity period…)"
          data-testid="rfq-notes" className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm mt-4 h-20" />
        <button onClick={submit} disabled={busy} data-testid="rfq-submit"
          className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white rounded-lg px-4 py-2 text-sm hover:bg-zinc-800 disabled:opacity-50">
          <Send className="h-4 w-4" /> {busy ? "Submitting…" : "Submit quote"}
        </button>
        <p className="text-[11px] text-zinc-400 mt-3">You are quoting scope + quantities only. Confirm field conditions before pricing.</p>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">{children}</div>
);
